// src/components/sections/Hero.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../hooks/useTheme';
import { ArrowRight, Code, Database, Server } from 'lucide-react';
import styles from './Hero.styles';

const Hero = () => {
  const { isDarkMode } = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }
    }
  };

  const statsVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: i => ({
      y: 0,
      opacity: 1,
      transition: { 
        delay: 0.5 + (i * 0.1), 
        duration: 0.7, 
        ease: [0.04, 0.62, 0.23, 0.98] 
      }
    })
  };

  const codeSnippetVariants = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { delay: 0.6, duration: 0.7, ease: [0.04, 0.62, 0.23, 0.98] }
    }
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={styles.contentGrid}
        >
          {/* Left Content */}
          <div style={styles.leftContent}>
            <motion.h1
              variants={itemVariants}
              style={styles.heading}
            >
              Build Smarter with Daftra’s Powerful API
            </motion.h1>

            <motion.p
              variants={itemVariants}
              style={styles.subheading(isDarkMode)}
            >
             Seamlessly connect, integrate, and extend your business workflows with our robust API — built by developers, for developers.
            </motion.p>

            <motion.div
              variants={itemVariants}
              style={styles.buttonContainer}
            >
              <a
                href="https://docs.daftara.dev/"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.primaryButton}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#2563eb';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#3b82f6';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Explore Documentation
                <ArrowRight size={18} />
              </a>

      
            </motion.div>

            {/* Stats */}
            <div style={styles.statsContainer}>
              {[
                { icon: <Server size={20} />, value: '99.9%', label: 'Uptime guarantee' },
                { icon: <Database size={20} />, value: '10M+', label: 'API requests daily' },
                { icon: <Code size={20} />, value: '200+', label: 'API endpoints' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  custom={index}
                  variants={statsVariants}
                  initial="hidden"
                  animate="visible"
                  style={styles.statCard(isDarkMode)}
                >
                  <div style={styles.statIcon}>
                    {stat.icon}
                  </div>
                  <span style={styles.statValue(isDarkMode)}>
                    {stat.value}
                  </span>
                  <span style={styles.statLabel(isDarkMode)}>
                    {stat.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;